import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { usePageTitle } from "@/components/common/AppShell";
import WizardShell from "@/components/common/WizardShell";
import Button from "@/components/common/Button";
import { useWizard } from "@/hooks/useWizard";
import { toApiDateTime } from "@/utils/dateUtils";
import {
  toFormState,
  toNullableInt,
  toNullableFloat,
} from "@/components/campaigns/wizard/campaignFormUtils";
import Step1Basics from "@/components/campaigns/wizard/Step1Basics";
import Step2Audience from "@/components/campaigns/wizard/Step2Audience";
import Step3Creatives from "@/components/campaigns/wizard/Step3Creatives";
import Step4Delivery from "@/components/campaigns/wizard/Step4Delivery";
import Step5Review from "@/components/campaigns/wizard/Step5Review";
import CampaignSummarySidebar from "@/components/campaigns/wizard/CampaignSummarySidebar";

const STEPS = [
  { id: "basics", label: "Basics" },
  { id: "audience", label: "Audience" },
  { id: "creatives", label: "Creatives" },
  { id: "delivery", label: "Budget & delivery" },
  { id: "review", label: "Review" },
];

export default function CampaignBuilderPage() {
  const navigate = useNavigate();
  const params = useParams();
  const editingUuid = params.uuid ?? null;
  const {
    campaigns,
    segments,
    addCampaign,
    updateCampaign,
    getCampaignCost,
    currentUser,
    currentUserType,
    submitCampaignForModeration,
    downloadCampaignPlan,
    performRequest,
  } = useApp();

  const editingCampaign = useMemo(
    () => (editingUuid ? campaigns.find((c) => c.uuid === editingUuid) : null),
    [campaigns, editingUuid],
  );

  const isEdit = Boolean(editingUuid);
  usePageTitle(isEdit ? "Edit campaign" : "Create campaign");

  const [form, setForm] = useState(() => toFormState(editingCampaign));
  const [isSaving, setIsSaving] = useState(false);
  const [isModerateSaving, setIsModerateSaving] = useState(false);
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);

  const [creatives, setCreatives] = useState([]);
  const [segmentCreatives, setSegmentCreatives] = useState({});

  const goBack = () => navigate("/dashboard/campaigns");

  const isStepValid = useCallback(
    (index) => {
      if (index === 0) return form.name.trim().length > 0;
      if (index === 1) {
        const frequency = Number(form.frequency);
        return Number.isInteger(frequency) && frequency > 0;
      }
      return true;
    },
    [form.name, form.frequency],
  );

  const wizard = useWizard({ stepCount: STEPS.length, isStepValid });

  useEffect(() => {
    setForm(toFormState(editingCampaign));
  }, [editingCampaign]);

  useEffect(() => {
    performRequest("/creatives")
      .then((data) => setCreatives(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load creatives", err));
  }, [performRequest]);

  useEffect(() => {
    if (!editingCampaign?.uuid) {
      setSegmentCreatives({});
      return;
    }
    performRequest(`/campaigns/${editingCampaign.uuid}/creatives`)
      .then((data) => {
        if (data && typeof data === "object") setSegmentCreatives(data);
      })
      .catch((err) => console.error("Failed to load campaign creatives", err));
  }, [editingCampaign?.uuid, performRequest]);

  useEffect(() => {
    if (!editingCampaign?.uuid) {
      setBudget(null);
      return;
    }
    let cancelled = false;
    setBudgetLoading(true);
    getCampaignCost(editingCampaign.uuid)
      .then((cost) => {
        if (!cancelled) setBudget(cost);
      })
      .catch((error) => {
        console.error("Failed to load campaign cost", error);
        if (!cancelled) setBudget(null);
      })
      .finally(() => {
        if (!cancelled) setBudgetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editingCampaign?.uuid, getCampaignCost]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (field, value) => {
    if (value === "") {
      handleFieldChange(field, "");
    } else {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return;
      handleFieldChange(field, numericValue);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        selectedSegments: form.selectedSegments,
        creativeDuration:
          form.creativeDuration === "" ? null : form.creativeDuration,
        creativeWidth: form.creativeWidth === "" ? null : form.creativeWidth,
        creativeHeight: form.creativeHeight === "" ? null : form.creativeHeight,
        startDate: toApiDateTime(form.startDate),
        endDate: toApiDateTime(form.endDate),
        frequency: form.frequency,
        cpm: Number(form.cpm) || 0,
        dailyFrequency: toNullableInt(form.dailyFrequency),
        weeklyFrequency: toNullableInt(form.weeklyFrequency),
        impressionsCap: toNullableInt(form.impressionsCap),
        budgetCap: toNullableFloat(form.budgetCap),
      };

      let saved;
      if (editingCampaign) {
        saved = await updateCampaign(editingCampaign.uuid, payload);
      } else {
        saved = await addCampaign({
          ...payload,
          createdBy: currentUser || "admin",
        });
      }

      const campaignUuid = saved?.uuid ?? editingCampaign?.uuid;
      const nonEmpty = Object.fromEntries(
        Object.entries(segmentCreatives).filter(([, v]) => v),
      );
      if (campaignUuid && Object.keys(nonEmpty).length > 0) {
        await performRequest(`/campaigns/${campaignUuid}/creatives`, {
          method: "PUT",
          body: JSON.stringify({ segmentCreatives: nonEmpty }),
        }).catch((err) =>
          console.error("Failed to save campaign creatives", err),
        );
      }

      goBack();
    } catch (error) {
      console.error("Failed to save campaign", error);
      if (typeof window !== "undefined") {
        window.alert(
          "Failed to save campaign. Please review the fields and try again.",
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleModerate = async () => {
    if (!editingCampaign?.uuid) return;

    setIsModerateSaving(true);
    try {
      await submitCampaignForModeration(editingCampaign.uuid);
    } catch (error) {
      console.error("Failed to submit for moderation", error);
    } finally {
      setIsModerateSaving(false);
    }
  };

  const handleDownloadPlan = async () => {
    if (!editingCampaign?.uuid) return;

    try {
      await downloadCampaignPlan(
        editingCampaign.uuid,
        form.name || editingCampaign.name || "Campaign",
      );
    } catch (error) {
      console.error("Failed to download media plan", error);
      window.alert("Failed to download media plan");
    }
  };

  if (isEdit && !editingCampaign) {
    return <p className="text-sm text-(--text-secondary)">Loading campaign…</p>;
  }

  const nextDisabled = !isStepValid(wizard.currentStep);

  const footer = (
    <>
      <Button
        variant="secondary"
        onClick={wizard.isFirstStep ? goBack : wizard.goBack}
        disabled={isSaving}
      >
        {wizard.isFirstStep ? "Cancel" : "Back"}
      </Button>
      {wizard.isLastStep ? (
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving
            ? isEdit
              ? "Saving…"
              : "Creating…"
            : isEdit
              ? "Save changes"
              : "Create campaign"}
        </Button>
      ) : (
        <Button onClick={wizard.goNext} disabled={nextDisabled}>
          Next: {STEPS[wizard.currentStep + 1].label}
        </Button>
      )}
    </>
  );

  return (
    <WizardShell
      backLabel="Back to campaigns"
      onBack={goBack}
      steps={STEPS}
      currentStep={wizard.currentStep}
      onStepChange={wizard.goToStep}
      canGoToStep={wizard.canGoToStep}
      summary={
        <CampaignSummarySidebar
          form={form}
          isEditMode={isEdit}
          budget={budget}
          budgetLoading={budgetLoading}
        />
      }
      footer={footer}
    >
      {wizard.currentStep === 0 && (
        <Step1Basics
          form={form}
          onFieldChange={handleFieldChange}
          canEditStatus={currentUserType === "admin"}
        />
      )}
      {wizard.currentStep === 1 && (
        <Step2Audience
          form={form}
          segments={segments}
          onFieldChange={handleFieldChange}
        />
      )}
      {wizard.currentStep === 2 && (
        <Step3Creatives
          form={form}
          segments={segments}
          creatives={creatives}
          onCreativeCreated={(created) =>
            setCreatives((prev) => [...prev, created])
          }
          segmentCreatives={segmentCreatives}
          onSegmentCreativesChange={setSegmentCreatives}
          onFieldChange={handleFieldChange}
          onNumberChange={handleNumberChange}
        />
      )}
      {wizard.currentStep === 3 && (
        <Step4Delivery
          form={form}
          onFieldChange={handleFieldChange}
          onNumberChange={handleNumberChange}
        />
      )}
      {wizard.currentStep === 4 && (
        <Step5Review
          form={form}
          segments={segments}
          creatives={creatives}
          segmentCreatives={segmentCreatives}
          isEditMode={isEdit}
          campaignUuid={editingCampaign?.uuid ?? null}
          onDownloadPlan={handleDownloadPlan}
          onModerate={handleModerate}
          isModerateSaving={isModerateSaving}
        />
      )}
    </WizardShell>
  );
}
